import { useEffect } from "react";

import { log } from "@/lib/log";
const BASE = "RiskPedia";
const TAGLINE = "The Risk Framework Encyclopedia & AI Advisor";
const SITE = "https://riskpedia.co.in";
const JSONLD_ID = "rp-jsonld";

function setOrCreateMeta(selector, attr, value) {
  let el = document.querySelector(selector);
  let created = false;
  if (!el) {
    const head = document.head;
    el = document.createElement("meta");
    // Parse simple selectors: meta[property="og:title"] or meta[name="description"]
    const m = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, prev, created };
}

function setCanonical(href) {
  let link = document.querySelector("link[rel='canonical']");
  let created = false;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
    created = true;
  }
  const prev = link.getAttribute("href");
  link.setAttribute("href", href);
  return { link, prev, created };
}

function injectJsonLd(payload) {
  let el = document.getElementById(JSONLD_ID);
  let created = false;
  if (!el) {
    el = document.createElement("script");
    el.id = JSONLD_ID;
    el.type = "application/ld+json";
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.textContent;
  el.textContent = JSON.stringify(payload);
  return { el, prev, created };
}

/**
 * Sets document title, meta description, canonical URL, Open Graph tags, and
 * (when `schema` is provided) injects a JSON-LD structured-data script.
 *
 * Backward compatible — `useSeo("Title", "desc")` works just like the old hook.
 *
 * @param {string|null} title
 * @param {string|null} description
 * @param {object} [opts]
 * @param {string} [opts.path]      Relative path used for canonical/OG URL (defaults to window.location.pathname)
 * @param {object} [opts.schema]    Schema.org JSON-LD object (will be injected as ld+json)
 * @param {boolean} [opts.noindex]  Sets <meta name="robots" content="noindex,follow"> — use on auth/admin/private pages so Google stops wasting crawl budget on them and they don't appear in SERPs.
 */
export default function useSeo(title, description, opts = {}) {
  const schemaKey = JSON.stringify(opts.schema || null);
  const noindex = !!opts.noindex;
  useEffect(() => {
    const path = opts.path ?? window.location.pathname;
    const url = `${SITE}${path}`;
    const fullTitle = title ? `${title} · ${BASE}` : `${BASE} — ${TAGLINE}`;

    const prevTitle = document.title;
    document.title = fullTitle;

    const desc = description || null;
    const restore = [];

    // Robots meta — only override if explicitly noindex (otherwise inherit
    // the site-wide "index,follow,max-snippet:-1,..." from index.html).
    if (noindex) {
      const r = setOrCreateMeta("meta[name=\"robots\"]", "content", "noindex,follow,max-snippet:-1");
      restore.push(() => r.created ? r.el.remove() : r.el.setAttribute("content", r.prev || "index, follow"));
    }

    // Meta description
    if (desc) {
      const r = setOrCreateMeta("meta[name=\"description\"]", "content", desc);
      restore.push(() => r.created ? r.el.remove() : r.el.setAttribute("content", r.prev || ""));
    }

    // Open Graph
    const og = [
      ["meta[property=\"og:title\"]", "content", fullTitle],
      ["meta[property=\"og:url\"]", "content", url],
      ...(desc ? [["meta[property=\"og:description\"]", "content", desc]] : []),
      ...(opts.image ? [["meta[property=\"og:image\"]", "content", opts.image.startsWith("http") ? opts.image : `${SITE}${opts.image}`]] : []),
    ];
    og.forEach(([sel, attr, value]) => {
      const r = setOrCreateMeta(sel, attr, value);
      restore.push(() => r.created ? r.el.remove() : r.el.setAttribute(attr, r.prev || ""));
    });

    // Twitter
    const tw = [
      ["meta[name=\"twitter:title\"]", "content", fullTitle],
      ...(desc ? [["meta[name=\"twitter:description\"]", "content", desc]] : []),
      ...(opts.image ? [
        ["meta[name=\"twitter:card\"]", "content", "summary_large_image"],
        ["meta[name=\"twitter:image\"]", "content", opts.image.startsWith("http") ? opts.image : `${SITE}${opts.image}`],
      ] : []),
    ];
    tw.forEach(([sel, attr, value]) => {
      const r = setOrCreateMeta(sel, attr, value);
      restore.push(() => r.created ? r.el.remove() : r.el.setAttribute(attr, r.prev || ""));
    });

    // Canonical
    const canon = setCanonical(url);
    restore.push(() => canon.created ? canon.link.remove() : canon.link.setAttribute("href", canon.prev || SITE));

    // JSON-LD
    if (opts.schema) {
      const j = injectJsonLd(opts.schema);
      restore.push(() => j.created ? j.el.remove() : (j.el.textContent = j.prev));
    }

    return () => {
      document.title = prevTitle;
      restore.reverse().forEach((fn) => {
        try {
          fn();
        } catch (err) {
          // Element already removed by a faster-firing effect — safe to ignore.
          log.warn("[useSeo] cleanup step failed (likely benign):", err);
        }
      });
    };
  // schemaKey (JSON.stringify of opts.schema) is in deps and re-triggers when the
  // schema changes by value — so we suppress the rule's structural complaint.
  // opts.path/opts.image are primitives; opts.schema is included via schemaKey.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, opts.path, opts.image, schemaKey, noindex]);
}
